import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Envelope, ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import api, { apiError } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PhoneInput from "@/components/PhoneInput";

export default function Login() {
  const { applyAuth } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("+255");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const go = async (fn) => {
    setLoading(true);
    try {
      const user = await fn();
      toast.success(`Welcome back, ${user.name}`);
      nav(homeFor(user.role));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const customerLogin = () =>
    go(async () => {
      const { data } = await api.post("/auth/customer/login", { phone, pin });
      return applyAuth(data);
    });

  const staffLogin = () =>
    go(async () => {
      const { data } = await api.post("/auth/login", { email, password });
      return applyAuth(data);
    });

  return (
    <div className="min-h-screen ok-bg grain relative flex items-center justify-center px-5 py-10">
      <Link to="/" data-testid="back-home" className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={18} /> Home
      </Link>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Logo className="h-14" />
        </Link>
        <div className="card-surface p-7 fade-up">
          <h1 className="font-display font-bold text-2xl mb-1">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-6">Access your OneKite account.</p>

          <Tabs defaultValue="customer">
            <TabsList className="grid grid-cols-2 w-full bg-muted/50 rounded-full p-1 mb-6">
              <TabsTrigger value="customer" data-testid="tab-customer" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Phone size={16} className="mr-2" /> Customer
              </TabsTrigger>
              <TabsTrigger value="staff" data-testid="tab-staff" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Envelope size={16} className="mr-2" /> Business / Admin
              </TabsTrigger>
            </TabsList>

            <TabsContent value="customer" className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Phone number</Label>
                <PhoneInput testid="login-phone" value={phone} onChange={setPhone} />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">PIN</Label>
                <Input data-testid="login-pin" type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="••••" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <Button data-testid="customer-login-btn" onClick={customerLogin} disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                Forgot your PIN? <Link to="/forgot" data-testid="forgot-pin-link" className="text-primary font-medium">Reset it</Link>
              </p>
              <p className="text-sm text-center text-muted-foreground">
                New to OneKite? <Link to="/register" className="text-primary font-medium">Create account</Link>
              </p>
              <p className="text-sm text-center text-muted-foreground">
                Have a OneKite card? <Link to="/claim" data-testid="claim-link" className="text-primary font-medium">Claim your account</Link>
              </p>
            </TabsContent>

            <TabsContent value="staff" className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Password</Label>
                <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" />
              </div>
              <Button data-testid="staff-login-btn" onClick={staffLogin} disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <p className="text-sm text-center text-muted-foreground">
                Register your business? <Link to="/business/register" className="text-primary font-medium">Start here</Link>
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
